// src/sim/ai/constants.ts — P4 AI top-level tunable constants (D-05, D-09).
// All values tick-based at 30 Hz. TODO(P7 POL-06): retune from playtest.
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-05, D-09; 04-RESEARCH.md Pattern 4.

/** D-05: per-role BT decide cadences. */
export const P4_HUNTER_DECIDE_HZ = 10;
export const P4_HIDER_DECIDE_HZ = 5;
export const P4_DOG_DECIDE_HZ = 4;
export const P4_ADULT_DECIDE_HZ = 2;

export const P4_HUNTER_DECIDE_PERIOD_TICKS = Math.round(30 / P4_HUNTER_DECIDE_HZ); // 3
export const P4_HIDER_DECIDE_PERIOD_TICKS = Math.round(30 / P4_HIDER_DECIDE_HZ);   // 6
export const P4_DOG_DECIDE_PERIOD_TICKS = Math.round(30 / P4_DOG_DECIDE_HZ);       // 8 (30/4=7.5, Math.round -> 8)
export const P4_ADULT_DECIDE_PERIOD_TICKS = Math.round(30 / P4_ADULT_DECIDE_HZ);   // 15

/** D-09: minimum light level under which a target is not considered visible to AI. */
export const P4_MIN_VISIBLE_LIGHT = 0.15;

/**
 * UAT Test 2 follow-up: ambient-light floor added to lightLevelAt() results
 * when used for AI perception (buildPerception + awareness step). The authored
 * map (mouchao.tmj) is intentionally sparse on permanent lights, and the
 * "wire each hunter's torch into its own perception" fix is explicitly
 * deferred past Phase 4 (see tick.ts Stage 16 agentFovBitset comment). Without
 * an ambient floor, hunters are effectively blind across ~95% of the map,
 * which contradicts Test 2's premise ("approach a hunter slowly, watch the
 * telegraph escalate"). 0.25 floor keeps hunters reactive across outdoor
 * areas while still leaving room for hiding-spot authoring to matter
 * (sightability × (ambient + local light) preserves the advantage of dark
 * interiors and bushes).
 *
 * Applied in src/sim/ai/perception.ts (visibility gate) and src/sim/tick.ts
 * Stage 17 (awareness sightability factor) — the two AI-facing read sites.
 * Does NOT touch the render-layer light mask or the player's own light
 * experience, so the night-aesthetic is unchanged.
 */
export const P4_AI_AMBIENT_LIGHT_FLOOR = 0.25;

/** D-09: hider/dog non-hunter role visible radius in tiles (fallback for roles without rot-js FOV). */
export const P4_HIDER_VISION_RADIUS_TILES = 6;
export const P4_DOG_FOV_RADIUS_TILES = 4;

/** D-09: awareness accumulator maxRange. */
export const P4_AWARENESS_MAX_RANGE_TILES = 14;

/** D-43: hearing boost only applies when velocity is below this threshold (px/s). */
export const P4_LISTEN_STILL_VELOCITY_PX_PER_S = 8;

/** D-10: flat listen base before hear.stat scaling (was implicit 0.30 in the flat formula). */
export const P4_LISTEN_BASE = 0.05;
/** D-10: hear.stat multiplier for listen utility — high-stat hunters listen more than low-stat ones.
 * listenBase = P4_LISTEN_BASE + P4_LISTEN_STAT_WEIGHT * profile.hear.stat
 * ze (0.70): 0.365 → listen 0.665 > check 0.65 (listens often)
 * tommy (0.50): 0.275 → listen 0.575 < check 0.65 (rarely listens)
 */
export const P4_LISTEN_STAT_WEIGHT = 0.45;

/** D-34: per-already-checked spot penalty on the check-hiding-spot branch utility (negative). */
export const P4_CHECKED_SPOT_PENALTY = -0.3;

/** D-13: boredom timer base ticks before a hiding hider exits (20s at 30 Hz). */
export const P4_BOREDOM_BASE_TICKS = 600;
/** D-13: extra ticks patient hiders (high hideSkill) wait before exiting. */
export const P4_BOREDOM_HIDE_SKILL_TICKS = 450;
/** D-13: ticks subtracted for alone-sensitive hiders (high fear.alone) — they get restless faster. */
export const P4_BOREDOM_ALONE_TICKS = 300;
/** D-13: ±% RNG jitter on boredom threshold so hiders don't all exit simultaneously. */
export const P4_BOREDOM_RNG_RANGE = 0.15;

// ---------------------------------------------------------------------------
// Dog constants (D-24, D-25, NPC-07)
// ---------------------------------------------------------------------------

/** D-24: simple radius detection for dogs (NOT rot-js FOV — dogs hear through walls). */
export const P4_DOG_CALM_RADIUS_TILES = 5;
/** D-24: per-dog bark cooldown in ticks (2 s at 30 Hz). */
export const P4_DOG_BARK_COOLDOWN_TICKS = 60;
/** D-24: wander interval — pick new waypoint every ~4 s. */
export const P4_DOG_WANDER_INTERVAL_TICKS = 120;

/**
 * Plan 06-13 (UAT Gap 5): post-drop-off patrol-base penalty window in ticks.
 * After applyDropOff sets `recentDropOffTick` on a hunter, scoreHunterBranches
 * zero-weights patrol-base for this many ticks so patrol-far (0.30) wins as
 * the lowest non-zero fallback and the hunter walks AWAY from the plinth
 * instead of restalling at map-centre with len<tileWidth in
 * PathTowardPatrolPoint.
 *
 * 90 ticks = 3 s at 30 Hz — long enough for an A* hop into a far-quarter
 * patrol target, short enough not to starve the patrol-base branch in
 * normal play. TODO(P7 POL-06): retune from playtest.
 */
export const P4_POST_DROPOFF_PATROL_BASE_PENALTY_TICKS = 90;

/**
 * Debug session freed-hiders-dont-disperse (2026-04-28): post-liberation
 * window in ticks during which a freed hider's `liberate` archetype score is
 * suppressed to 0. Without this suppression, a freed hider lingering in or
 * near the plinth zone repeatedly re-picks the liberate branch, walks back to
 * the plinth target, re-fires free-all, and creates a self-sustaining
 * oscillation visible as audio source / bubble cascade.
 *
 * Computed off the entity's `freedImmunityUntilTick` field, which is set by
 * applyFreeAll to `currentTick + P3_FREED_IMMUNITY_TICKS`. The suppression
 * window starts at the moment of liberation and lasts for this many ticks
 * total — i.e. `recentlyFreed = currentTick < (freedImmunityUntilTick -
 * P3_FREED_IMMUNITY_TICKS) + P4_POST_LIBERATION_LIBERATE_SUPPRESSION_TICKS`.
 *
 * 90 ticks = 3 s at 30 Hz — long enough to walk ~3-4 tiles toward a hiding
 * spot, well past the plinth zone boundary on Mouchao. TODO(P7 POL-06):
 * retune from playtest. Resets to null on team-switch (round flip).
 */
export const P4_POST_LIBERATION_LIBERATE_SUPPRESSION_TICKS = 90;

// ---------------------------------------------------------------------------
// Adult constants (D-28, NPC-04)
// ---------------------------------------------------------------------------

/** D-28: global adult commentary cooldown in ticks (10 s at 30 Hz). */
export const P4_ADULT_COMMENTARY_GLOBAL_COOLDOWN_TICKS = 300;
